// Visibility state management for form fields
import React, { useState, useEffect } from 'react';

// Global visibility state manager for form fields
interface VisibilityState {
  [fieldName: string]: boolean;
}

// Create a global visibility state
const fieldVisibility: VisibilityState = {};

/**
 * Updates the visibility state of a field and notifies listeners
 * @param fieldName The name of the field to update
 * @param isVisible Whether the field should be visible
 */
export const updateFieldVisibility = (
  fieldName: string,
  isVisible: boolean
) => {
  fieldVisibility[fieldName] = isVisible;
  // Dispatch custom event to notify components
  window.dispatchEvent(
    new CustomEvent('field-visibility-change', {
      detail: { fieldName, isVisible }
    })
  );
};

/**
 * Checks if a field is currently visible
 * @param fieldName The name of the field to check
 * @returns True if the field is visible, false otherwise
 */
export const isFieldVisible = (fieldName: string): boolean => {
  return fieldVisibility[fieldName] !== false; // Default to visible
};

/**
 * Hook to use and respond to field visibility changes
 * @param fieldName The name of the field to watch
 * @returns Current visibility state of the field
 */
export const useFieldVisibility = (fieldName: string) => {
  const [isVisible, setIsVisible] = useState(() => isFieldVisible(fieldName));

  useEffect(() => {
    // Handler for visibility change events
    const handleVisibilityChange = (event: Event) => {
      const customEvent = event as CustomEvent<{
        fieldName: string;
        isVisible: boolean;
      }>;
      if (customEvent.detail.fieldName === fieldName) {
        setIsVisible(customEvent.detail.isVisible);
      }
    };

    // Listen for visibility changes
    window.addEventListener('field-visibility-change', handleVisibilityChange);

    // Cleanup on unmount
    return () => {
      window.removeEventListener(
        'field-visibility-change',
        handleVisibilityChange
      );
    };
  }, [fieldName]);

  return isVisible;
};
